import React, { useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, Box, TextField, Grid, IconButton, Tooltip } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import branding from '../../styles/branding';
import useApi from '../../hooks/useApi';
import ColumnSelector from './ColumnSelector';
import WidgetResultsRenderer from './WidgetResultsRenderer';
import AiExecutionLoader from './AiExecutionLoader';
import WidgetConfigModal from './WidgetConfigModal';
import { DEFAULT_PROMPT_TYPE } from '../../constants/promptTypes';
import { DEFAULT_OUTPUT_MODE } from '../../constants/outputModes';
import { useTenantDefaultAiModel } from '../../hooks/useTenantDefaultAiModel';
import './AIRecommendation.css';

const AIRecommendation = ({ open, onClose, onAddToDashboard, jobId, initialData, onExecute, editWidgetId, onUpdateCard }) => {
  const tenantDefaultModel = useTenantDefaultAiModel();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [aiResults, setAiResults] = useState(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [widgetName, setWidgetName] = useState('');
  const [selectedModel, setSelectedModel] = useState(() => tenantDefaultModel);
  const [selectedPromptType, setSelectedPromptType] = useState(DEFAULT_PROMPT_TYPE);
  const [selectedOutputMode, setSelectedOutputMode] = useState(DEFAULT_OUTPUT_MODE);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const api = useApi();
  const widgetIdRef = useRef(null);

  React.useEffect(() => {
    if (!open) return;
    if (initialData) {
      if (initialData.widgetName) setWidgetName(initialData.widgetName);
      if (Array.isArray(initialData.selectedColumns) && initialData.selectedColumns.length) setSelectedColumns(initialData.selectedColumns);
      if (initialData.widgetResults) setAiResults(initialData.widgetResults);
      setSelectedModel(initialData.selectedModel || tenantDefaultModel);
      if (initialData.selectedPromptType) setSelectedPromptType(initialData.selectedPromptType);
      if (initialData.selectedOutputMode) setSelectedOutputMode(initialData.selectedOutputMode);
    } else {
      setSelectedModel(tenantDefaultModel);
    }
  }, [open, initialData, tenantDefaultModel]);

  const handleColumnsChange = (columns) => {
    setSelectedColumns(columns);
  };

  const getWidgetId = () => {
    if (editWidgetId) return editWidgetId;
    if (initialData?.widgetId) return initialData.widgetId;
    if (!widgetIdRef.current) {
      const cryptoObj = window?.crypto;
      widgetIdRef.current = cryptoObj?.randomUUID
        ? cryptoObj.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    return widgetIdRef.current;
  };

  const handleExecuteAI = async () => {
    if (!jobId) {
      setAiError('JobID not found. Please try again.');
      return;
    }

    if (selectedColumns.length === 0) {
      setAiError('Please select at least one column to analyze.');
      return;
    }

    if (!widgetName.trim()) {
      setAiError('Please enter a card name before executing AI analysis.');
      return;
    }

    setIsLoadingAI(true);
    setAiError(null);
    setAiResults(null);

    try {
      const requestData = {
        jobId: jobId, // Keep as string (UUID)
        widgetType: 'recommendation',
        selectedColumns: selectedColumns,
        widgetName: widgetName.trim(),
        model: selectedModel,
        promptType: selectedPromptType,
        outputMode: selectedOutputMode,
        widgetId: getWidgetId()
      };
      const response = await api.post("/widgets/execute", requestData);

      console.log('AI execution response:', response.data);
      setAiResults(response.data?.data || response.data);
      onExecute?.();

      if (response.data?.data?.result_id || response.data?.result_id) {
        console.log(`Widget result stored with ID: ${response.data?.data?.result_id || response.data?.result_id}`);
      }
    } catch (error) {
      console.error('Error executing AI:', error);
      setAiError("We couldn't complete this analysis just now. Please try again in a moment, or open configuration and choose a different model.");
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAddToDashboard = () => {
    if (selectedColumns.length > 0 && widgetName.trim()) {
      onAddToDashboard({
        widgetId: getWidgetId(),
        widgetType: 'recommendation',
        cardType: 'AI Recommendation',
        selectedColumns: selectedColumns,
        results: aiResults,
        widgetName: widgetName.trim(),
        selectedModel,
        selectedPromptType,
        selectedOutputMode
      });
      onClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={branding.dialog?.PaperProps}
      slotProps={branding.dialog?.slotProps}
      className="ai-recommendation-dialog"
    >
      <DialogTitle className="ai-recommendation-title">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box className="ai-recommendation-title-content">
            <LightbulbIcon className="ai-recommendation-icon" />
            <Typography variant="h6" component="span">AI Recommendations</Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="ai-recommendation-content">
        <Grid container spacing={2} className="ai-recommendation-inputs" sx={{ alignItems: 'flex-start' }}>
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1, minHeight: '1.25rem' }}>
              <Typography
                variant="subtitle2"
                sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666666', fontFamily: branding.fonts.primary }}
              >
                Card Name
                <Box component="span" sx={{ color: '#666666', ml: 0.25 }}>*</Box>:
              </Typography>
            </Box>
            <TextField
              fullWidth
              value={widgetName}
              onChange={(e) => setWidgetName(e.target.value)}
              placeholder="Enter a name for this AI recommendation card"
              variant="outlined"
              required
              helperText="Give your card a descriptive name"
              sx={{ mt: 0 }}
              inputProps={{ 'aria-label': 'Card Name' }}
              InputProps={{
                startAdornment: <LightbulbIcon sx={{ mr: 1, color: '#000000', fontSize: '1.25rem' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
            <ColumnSelector
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              jobId={jobId}
              widgetType="ai_recommendation"
            />
          </Grid>
        </Grid>

        {aiError && (
          <Box className="widget-ai-friendly-message" role="alert">
            <Typography variant="body2">{aiError}</Typography>
          </Box>
        )}

        {isLoadingAI && <AiExecutionLoader />}

        {aiResults && (
          <WidgetResultsRenderer
            results={aiResults}
            widgetType="recommendation"
            showProcessingInfo={true}
          />
        )}
      </DialogContent>
      <DialogActions className="ai-recommendation-actions" sx={{ justifyContent: 'space-between' }}>
        <Tooltip title="Choose configuration">
          <IconButton size="small" onClick={() => setIsConfigModalOpen(true)} aria-label="Choose configuration" sx={{ ml: -0.5 }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          variant={branding.button.variant}
          size={branding.button.size}
          sx={branding.button.sx}
          onClick={handleExecuteAI}
          disabled={isLoadingAI || selectedColumns.length === 0}
          startIcon={isLoadingAI ? <CircularProgress size={20} /> : null}
        >
          {isLoadingAI ? 'Executing AI...' : 'Execute AI Analysis'}
        </Button>
        {editWidgetId && onUpdateCard ? (
          <Button
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={() => onUpdateCard({ widgetName: widgetName.trim(), selectedColumns, widgetType: 'recommendation', results: aiResults, selectedModel, selectedPromptType, selectedOutputMode })}
            disabled={selectedColumns.length === 0 || !widgetName.trim() || isLoadingAI}
          >
            Update card
          </Button>
        ) : (
          <Button 
            variant={branding.button.variant}
            size={branding.button.size}
            sx={branding.button.sx}
            onClick={handleAddToDashboard}
            disabled={selectedColumns.length === 0 || !widgetName.trim() || isLoadingAI || aiResults === null}
          >
            {initialData ? 'Edit Card' : 'Add Card'}
          </Button>
        )}
        </Box>
      </DialogActions>
    </Dialog>
    <WidgetConfigModal
      open={isConfigModalOpen}
      onClose={() => setIsConfigModalOpen(false)}
      selectedModel={selectedModel}
      selectedPromptType={selectedPromptType}
      selectedOutputMode={selectedOutputMode}
      onSaveConfig={(config) => {
        if (config?.model != null) setSelectedModel(config.model);
        if (config?.promptType != null) setSelectedPromptType(config.promptType);
        if (config?.outputMode != null) setSelectedOutputMode(config.outputMode);
      }}
    />
    </>
  );
};

export default AIRecommendation;
